/**
 * Bottom Cart Action Toast Controller
 * Displays notifications for Add, Remove, and Update cart operations matching Reference Photo 2.
 */

class CartToastComponent extends HTMLElement {
  #autoDismissDelay = 1500;
  #dismissTimeout = null;
  #hideTimeout = null;
  #isHovered = false;

  connectedCallback() {
    this.inner = this.querySelector('.cart-toast__inner');
    this.image = this.querySelector('.cart-toast__image');
    this.mediaWrapper = this.querySelector('.cart-toast__media-wrapper');
    this.message = this.querySelector('.cart-toast__message');
    this.closeBtn = this.querySelector('#CartToastClose');
    this.goToCartBtn = this.querySelector('#CartToastGoToCart');

    this.closeBtn?.addEventListener('click', () => this.hide());
    this.goToCartBtn?.addEventListener('click', (e) => this.#handleGoToCart(e));

    this.addEventListener('mouseenter', () => {
      this.#isHovered = true;
      this.#clearDismissTimer();
    });

    this.addEventListener('mouseleave', () => {
      this.#isHovered = false;
      this.#startDismissTimer();
    });

    // Listen to custom show events
    document.addEventListener('cart-toast:show', (e) => {
      if (e.detail) {
        this.show(e.detail);
      }
    });

    // Global reference for direct invocation
    window.showCartToast = (detail) => this.show(detail);

    // Bind theme cart events and fetch interceptor
    this.#setupEventListeners();
  }

  #lastActionTime = 0;
  #lastActionType = '';

  /**
   * Displays the toast notification
   * @param {Object} options
   * @param {'add'|'remove'|'update'} options.action
   * @param {string} [options.image]
   * @param {string} [options.title]
   */
  show({ action = 'add', image, title = '' }) {
    if (!this.message) return;

    // Clear any pending hide animation
    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = null;
    }
    this.#clearDismissTimer();

    // Debounce exact duplicates within 300ms
    const now = Date.now();
    if (now - this.#lastActionTime < 300 && this.#lastActionType === action) {
      this.#startDismissTimer();
      return;
    }
    this.#lastActionTime = now;
    this.#lastActionType = action;

    // Set message based on action
    if (action === 'remove') {
      this.message.innerHTML = '<span class="cart-toast__text">Product <span class="cart-toast__highlight">removed</span> from cart</span>';
    } else if (action === 'update') {
      this.message.innerHTML = '<span class="cart-toast__text">Quantity updated</span>';
    } else {
      this.message.innerHTML = '<span class="cart-toast__text">Product added to cart</span>';
    }

    // Resolve image URL
    let resolvedImage = image;
    if (typeof resolvedImage === 'object' && resolvedImage !== null) {
      resolvedImage = resolvedImage.url || resolvedImage.src || '';
    }
    if (typeof resolvedImage === 'string' && resolvedImage.startsWith('//')) {
      resolvedImage = 'https:' + resolvedImage;
    }

    // Set thumbnail
    if (this.image && resolvedImage) {
      this.image.src = resolvedImage;
      this.image.alt = title || 'Product';
      if (this.mediaWrapper) this.mediaWrapper.style.display = 'flex';
    } else if (this.mediaWrapper) {
      // If we don't have an image for this item, hide the media wrapper gracefully
      this.mediaWrapper.style.display = 'none';
    }

    // Trigger animation
    this.classList.remove('cart-toast--hiding');
    this.classList.add('cart-toast--visible');

    // Start auto-dismiss timer
    this.#startDismissTimer();
  }

  hide() {
    this.#clearDismissTimer();
    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = null;
    }
    this.classList.add('cart-toast--hiding');
    this.#hideTimeout = setTimeout(() => {
      this.classList.remove('cart-toast--visible', 'cart-toast--hiding');
      this.#hideTimeout = null;
    }, 380);
  }

  #startDismissTimer() {
    this.#clearDismissTimer();
    if (this.#isHovered) return;

    this.#dismissTimeout = setTimeout(() => {
      this.hide();
    }, this.#autoDismissDelay);
  }

  #clearDismissTimer() {
    if (this.#dismissTimeout) {
      clearTimeout(this.#dismissTimeout);
      this.#dismissTimeout = null;
    }
  }

  #handleGoToCart(event) {
    event.preventDefault();
    this.hide();

    // Try opening cart drawer if available
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    // Fallback: Click cart drawer trigger
    const drawerTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]') ||
                          document.querySelector('#CartDrawerTrigger') ||
                          document.querySelector('.cart-drawer-wrapper button') ||
                          document.querySelector('a[href="/cart"]');
    if (drawerTrigger) {
      drawerTrigger.click();
      return;
    }

    // Full navigation fallback
    window.location.href = '/cart';
  }

  #setupEventListeners() {
    const self = this;

    // Intercept form submissions for /cart/add
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.action && form.action.includes('/cart/add')) {
        const card = form.closest('.product-card') || form.closest('product-card') || form.closest('.product-information') || form.closest('section');
        const img = card?.querySelector('.product-image img') || card?.querySelector('img');
        const title = card?.querySelector('.product-title a')?.textContent?.trim() ||
                      card?.querySelector('h1')?.textContent?.trim() || '';

        if (img?.src) {
          self.#lastActionProduct = { image: img.src, title };
        }
      }
    }, true);

    // Also monitor button clicks on product cards
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="add"], .buy-btn, .product-btn, [data-add-to-cart], .add-to-cart-button');
      if (btn) {
        const card = btn.closest('.product-card') || btn.closest('product-card') || btn.closest('.product-information') || btn.closest('section');
        const img = card?.querySelector('.product-image img') || card?.querySelector('img');
        const title = card?.querySelector('.product-title a')?.textContent?.trim() ||
                      card?.querySelector('h1')?.textContent?.trim() || '';

        if (img?.src) {
          self.#lastActionProduct = { image: img.src, title };
        }
      }
    }, true);

    // Listen to theme's cart:update event as safety net for product-form-component
    document.addEventListener('cart:update', (e) => {
      if (e.detail?.data?.source === 'product-form-component' && !e.detail?.data?.didError) {
        const productId = e.detail?.data?.productId;
        let img = self.#lastActionProduct?.image;
        let title = self.#lastActionProduct?.title;
        if (!img && productId) {
          const card = document.querySelector(`[data-product-id="${productId}"]`);
          img = card?.querySelector('.product-image img')?.src || card?.querySelector('img')?.src;
          title = card?.querySelector('.product-title a')?.textContent?.trim() || card?.querySelector('h1')?.textContent?.trim();
        }
        self.show({ action: 'add', image: img, title });
      }
    });

    // Intercept native fetch calls to /cart/add.js, /cart/change.js, etc.
    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
      const response = await originalFetch.apply(this, args);

      // Card quantity selector triggers instant optimistic toast on click, so don't double trigger
      const isCardQtySource = url.includes('source=card-quantity-selector') ||
        (args[1]?.headers && (
          args[1].headers['X-Requested-With'] === 'card-quantity-selector' ||
          (args[1].headers instanceof Headers && args[1].headers.get('X-Requested-With') === 'card-quantity-selector')
        ));

      if (isCardQtySource) {
        return response;
      }

      const isCartItemsComponent =
        (args[1]?.headers && (
          args[1].headers['X-Requested-With'] === 'cart-items-component' ||
          (args[1].headers instanceof Headers && args[1].headers.get('X-Requested-With') === 'cart-items-component')
        ));

      try {
        if (url.includes('/cart/add')) {
          const clone = response.clone();
          clone.json().then((data) => {
            const image = data.image || data.featured_image || self.#lastActionProduct?.image || '';
            const title = data.title || data.product_title || self.#lastActionProduct?.title || '';
            self.show({ action: 'add', image, title });
          }).catch(() => {
            if (self.#lastActionProduct) {
              self.show({ action: 'add', ...self.#lastActionProduct });
            }
          });
        } else if (url.includes('/cart/change') || url.includes('/cart/update')) {
          const clone = response.clone();
          clone.json().then((cart) => {
            const body = args[1]?.body;
            let targetQuantity = null;

            if (typeof body === 'string') {
              try {
                const parsed = JSON.parse(body);
                targetQuantity = parsed.quantity;
              } catch (_) {
                const params = new URLSearchParams(body);
                if (params.has('quantity')) targetQuantity = parseInt(params.get('quantity'), 10);
              }
            }

            if (!isCartItemsComponent) {
              if (targetQuantity === 0) {
                self.show({ action: 'remove', image: self.#lastActionProduct?.image, title: self.#lastActionProduct?.title });
              } else {
                self.show({ action: 'update', image: self.#lastActionProduct?.image, title: self.#lastActionProduct?.title });
              }

              // Broadcast cart:update so theme components and product cards remain synchronized
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  data: {
                    source: 'cart-toast',
                    itemCount: cart.item_count,
                    sections: cart.sections || undefined
                  }
                }
              }));
            }
          }).catch(() => {});
        }
      } catch (err) {
        console.error('CartToast fetch intercept error:', err);
      }

      return response;
    };
  }

  #lastActionProduct = null;
}

if (!customElements.get('cart-toast')) {
  customElements.define('cart-toast', CartToastComponent);
}
